/*
 * Copyright (c) 2026 Paxia AI Pty Ltd. All rights reserved.
 * This software is proprietary and confidential.
 * No part of this codebase may be copied, modified, published, distributed, sublicensed, or used without prior written permission from Paxia AI Pty Ltd.
 */

import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";

import { getMyAccess } from "../api/subscriptions";
import { evaluateSurfaceAccess } from "../access/surfaces";

const SubscriptionAccessContext = createContext(null);

function getStoredToken() {
  try {
    return localStorage.getItem("token");
  } catch {
    return null;
  }
}

function getStoredUserStaffFlag() {
  try {
    const raw = localStorage.getItem("user");
    if (!raw) {
      return false;
    }
    const parsed = JSON.parse(raw);
    return parsed?.is_staff === true;
  } catch {
    return false;
  }
}

function buildFallbackSnapshot() {
  return {
    user: {
      is_authenticated: true,
      is_staff: getStoredUserStaffFlag(),
    },
    subscription: { status: "unknown" },
    features: {},
    surfaces: {},
    rollout_mode: "allow_unconfigured_features",
  };
}

export function SubscriptionAccessProvider({ children }) {
  const [authToken, setAuthToken] = useState(() => getStoredToken());
  const [accessSnapshot, setAccessSnapshot] = useState(null);
  const [isLoading, setIsLoading] = useState(Boolean(getStoredToken()));

  const refreshAccess = useCallback(async () => {
    const token = getStoredToken();
    setAuthToken(token);
    if (!token) {
      setAccessSnapshot(null);
      setIsLoading(false);
      return null;
    }

    setIsLoading(true);
    try {
      const snapshot = await getMyAccess();
      setAccessSnapshot(snapshot);
      return snapshot;
    } catch {
      const fallback = buildFallbackSnapshot();
      setAccessSnapshot(fallback);
      return fallback;
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    refreshAccess();
  }, [refreshAccess]);

  useEffect(() => {
    const sync = () => {
      refreshAccess();
    };
    const onStorage = (event) => {
      if (!event || (event.key !== "token" && event.key !== "user")) {
        return;
      }
      sync();
    };

    window.addEventListener("paxia:login", sync);
    window.addEventListener("paxia:logout", sync);
    window.addEventListener("paxia:subscription-changed", sync);
    window.addEventListener("storage", onStorage);

    return () => {
      window.removeEventListener("paxia:login", sync);
      window.removeEventListener("paxia:logout", sync);
      window.removeEventListener("paxia:subscription-changed", sync);
      window.removeEventListener("storage", onStorage);
    };
  }, [refreshAccess]);

  const getSurfaceDecision = useCallback((surface, options = {}) => {
    const allowWhileLoading = options.allowWhileLoading !== false;
    if (authToken && isLoading && !accessSnapshot) {
      return {
        allowed: allowWhileLoading,
        configured: true,
        pending: true,
        reason: allowWhileLoading ? null : "loading",
        featureCode: surface?.featureCode || null,
      };
    }
    return {
      ...evaluateSurfaceAccess(surface, accessSnapshot),
      pending: false,
    };
  }, [accessSnapshot, authToken, isLoading]);

  const value = useMemo(() => ({
    authToken,
    accessSnapshot,
    isLoading,
    refreshAccess,
    getSurfaceDecision,
    canAccessSurface: (surface, options) => getSurfaceDecision(surface, options),
  }), [accessSnapshot, authToken, getSurfaceDecision, isLoading, refreshAccess]);

  return (
    <SubscriptionAccessContext.Provider value={value}>
      {children}
    </SubscriptionAccessContext.Provider>
  );
}

export function useSubscriptionAccess() {
  const context = useContext(SubscriptionAccessContext);
  if (!context) {
    throw new Error("useSubscriptionAccess must be used within SubscriptionAccessProvider");
  }
  return context;
}